import { useRef } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { Card, Row, Col, Form, Button as BSButton } from "react-bootstrap";
import ReactTextareaAutosize from "react-textarea-autosize";
import { ProviderModel, ProviderTypeEnum } from "../../data/entities";
import { usePageActions } from "../PageLayout";
import { PermissionDefinitions } from "../../security";
import { ProviderService, UserLocationService, UserService } from "../../services";
import { useUrlParams,
  useAsync,
  useCRUDHandlers,
  useSetPageTitle,
  usePermission,
  useTenantFeatures } from "../../hooks";
import { Button,
  HandleDropDownListChange,
  IconAndText,
  IconDefinitions,
  MediaUploader,
  SearchableProviderTypeDropdown,
  SearchableUserDropdown } from "../../components";
import { useMedia } from "./hooks/useProfilePicture";
import { useSelector } from "react-redux";
import { getUserInfo} from "../../app";
import "./ProviderDetailsPage.scss";

interface ProviderDetailsPageProps {
}

export function ProviderDetailsPage(props: Readonly<ProviderDetailsPageProps>) {

  const locationId = parseInt(useUrlParams("id"));
  const providerService = new ProviderService();
  const providerId = parseInt(useUrlParams("providerId"));
  const {value: provider} = useAsync(() => providerService.get(providerId), [providerId]);
  const { editItem } = useCRUDHandlers<ProviderModel>(providerService, "Provider");
  const submitRef = useRef<HTMLButtonElement>(null);
  const canEditProvider = usePermission(PermissionDefinitions.provider.edit(locationId!));
  const { manageProviders } = useTenantFeatures();
  const {
      previewImage,
      uploadImage,
      removeImage,
      fetchImage
    } = useMedia({
      providerId: providerId,
      onUploadSuccess: async () => {
        await fetchImage();
      },
    });

  const userLocationService = new UserLocationService();
  const userLocations = useSelector(getUserInfo())?.locationIds;

  const locations = userLocations?.join(",") ?? "";
  const { value: users } = useAsync(async () => await userLocationService.getUsersByLocationAsync(locations));

  const { value: providerUser } = useAsync(
    async () => (!manageProviders && provider?.userId ? await new UserService().get(provider.userId) : null),
    [provider?.userId, manageProviders]
  );

  let userSet = new Set<number>();
  users?.forEach(user => userSet.add(user.userId));
  const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    providerType: yup.string()
      .oneOf(Object.values(ProviderTypeEnum))
      .required("Provider Type is required"),
    description: yup.string().notRequired(),
    capacity: yup.number().min(0, "Capacity must be greater than or equal to 0").required("Capacity is required"),
    priority: yup.number().min(0, "Priority must be greater than or equal to 0").required("Priority is required"),
    isActive: yup.boolean().notRequired(),
    userId: yup.number().when('providerType', (providerType: ProviderTypeEnum | any[], schema) => {
      return (Object.values(ProviderTypeEnum) as ProviderTypeEnum[]).includes(providerType as ProviderTypeEnum)
        ? schema.required("User is required")
        : schema.notRequired();
    })
  });


  useSetPageTitle("Details", [providerId]);
  usePageActions(
    canEditProvider && (
      <Button onClick={() => submitRef.current?.click()}>
        <IconAndText iconName={IconDefinitions.save} text="Save" />
      </Button>
    ),
    [canEditProvider]
  );

  if(provider === null || provider === undefined) return null;

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        name: provider.name,
        providerType: provider.providerType,
        description: provider.description,
        capacity: provider.capacity,
        priority: provider.priority ?? 0,
        isActive: provider.isActive ?? true,
        userId: provider.userId,
      }}
      onSubmit={(values) => {
        editItem({
          ...provider,
          ...values,
          userId: values.providerType === ProviderTypeEnum.person ? values.userId : null,
          priority: values.priority,
          tenantId: provider.tenantId,
        })
      }}
    >
      {({ values, errors, touched, handleChange, setFieldValue, handleSubmit }) => (
        <Form onSubmit={handleSubmit} className="provider-details-page">
          <Card className="mb-3">
            <Card.Header>
              <h6 className="text-uppercase fw-semibold small mb-0">Identity</h6>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group controlId="providerType">
                    {manageProviders ? (
                      <>
                        <SearchableProviderTypeDropdown
                          name="providerType"
                          value={values.providerType}
                          placeholder="Type"
                          onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                          isInvalid={touched.providerType && !!errors.providerType}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.providerType}
                        </Form.Control.Feedback>
                      </>
                    ) : (
                      <>
                        <Form.Label className="small text-body-secondary mb-1">Type</Form.Label>
                        <Form.Control
                          plaintext
                          readOnly
                          className="fw-semibold p-0"
                          value={values.providerType ?? ""}
                        />
                      </>
                    )}
                  </Form.Group>
                </Col>
                {values.providerType === ProviderTypeEnum.person && (
                  <Col md={12}>
                    <Form.Group controlId="userId">
                      {manageProviders ? (
                        <>
                          <SearchableUserDropdown
                            name="userId"
                            value={values.userId}
                            placeholder="User"
                            onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                            isInvalid={touched.userId && !!errors.userId}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.userId}
                          </Form.Control.Feedback>
                        </>
                      ) : (
                        <>
                          <Form.Label className="small text-body-secondary mb-1">User</Form.Label>
                          <Form.Control
                            plaintext
                            readOnly
                            className="fw-semibold p-0"
                            value={providerUser?.displayName ?? ""}
                          />
                        </>
                      )}
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>
              <h6 className="text-uppercase fw-semibold small mb-0">Profile</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group controlId="name" className="mb-3">
                <Form.Label className="small text-body-secondary mb-1">Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  disabled={!manageProviders}
                  isInvalid={touched.name && !!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="description" className="mb-3">
                <Form.Label className="small text-body-secondary mb-1">Description</Form.Label>
                <ReactTextareaAutosize
                  minRows={4}
                  className="form-control"
                  placeholder="Add a short description..."
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  disabled={!manageProviders}
                />
              </Form.Group>
              <MediaUploader
                title="Profile picture"
                hint="PNG or JPG, up to 5 MB."
                previewImage={previewImage}
                uploadHandler={uploadImage}
                removeHandler={removeImage}
                onUploadSuccess={fetchImage}
              />
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>
              <h6 className="text-uppercase fw-semibold small mb-0">Configuration</h6>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group controlId="capacity">
                    <Form.Label className="small text-body-secondary mb-1">Capacity</Form.Label>
                    <Form.Control
                      type="number"
                      name="capacity"
                      placeholder="Capacity"
                      value={values.capacity}
                      onChange={handleChange}
                      disabled={!manageProviders}
                      isInvalid={touched.capacity && !!errors.capacity}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.capacity}
                    </Form.Control.Feedback>
                    <Form.Text className="text-body-secondary">Concurrent bookings allowed.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="priority">
                    <Form.Label className="small text-body-secondary mb-1">Priority</Form.Label>
                    <Form.Control
                      type="number"
                      name="priority"
                      placeholder="Priority"
                      value={values.priority}
                      onChange={handleChange}
                      disabled={!manageProviders}
                      isInvalid={touched.priority && !!errors.priority}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.priority}
                    </Form.Control.Feedback>
                    <Form.Text className="text-body-secondary">Lower numbers are offered first.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>
              <h6 className="text-uppercase fw-semibold small mb-0">Availability</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div className="me-3">
                  <p className="fw-semibold mb-0">Active</p>
                  <p className="small text-body-secondary mb-0">
                    Inactive providers are hidden from the schedule and calendar views and cannot accept new bookings. Existing bookings are unaffected.
                  </p>
                </div>
                <div className="form-check form-switch fs-4 flex-shrink-0 ps-0">
                  <input
                    className="form-check-input ms-0 mt-0"
                    type="checkbox"
                    role="switch"
                    checked={values.isActive}
                    onChange={() => setFieldValue("isActive", !values.isActive)}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>

          <BSButton ref={submitRef} type="submit" className="d-none" />
        </Form>
      )}
    </Formik>
  );
}

export default ProviderDetailsPage;
